import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Typography,
} from "@mui/material";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment, { type Moment } from "moment";
import { useCallback, useRef, useState } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useShiftsData } from "../hooks/useShifts";
import { cobblesRate, type ShiftsRow } from "../models/Shifts";
import { ShiftsToolbar } from "./ShiftsToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 28;
const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">No production was recorded in the selected range</span>`;

const DASH = "—";

const DURATION_COLUMNS = new Set([
	"DelaysDuration",
	"BarSetupDelaysDuration",
	"CobblesDuration",
]);

// Seconds as h:m:s, the way the legacy grid renders every duration on this
// screen: all three parts padded to two digits. Hours are not capped at 24 —
// a week of stops runs well past it.
const asDuration = (seconds: number | null | undefined): string => {
	if (seconds == null) return DASH;
	const total = Math.max(0, Math.round(seconds));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	return [h, m, s].map((part) => String(part).padStart(2, "0")).join(":");
};

const asPercent = (value: number | null | undefined): string =>
	value == null ? DASH : `${Math.round(value)}%`;

const asInteger = (value: number | null | undefined): string =>
	value == null ? DASH : value.toLocaleString("en-US");

const asNumber = (value: number | null | undefined): string =>
	value == null
		? DASH
		: value.toLocaleString("en-US", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});

const COLUMN_DEFS: ColDef<ShiftsRow>[] = [
	{
		field: "CrewId",
		headerName: "Shift",
		flex: 8,
		minWidth: 80,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductsCount",
		headerName: "Products",
		flex: 10,
		minWidth: 100,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asInteger(p.value),
	},
	{
		field: "Tonnage",
		headerName: "Tonnage",
		flex: 12,
		minWidth: 110,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "ProductChangesCount",
		headerName: "Product Changes",
		flex: 13,
		minWidth: 140,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asInteger(p.value),
	},
	{
		field: "DelaysDuration",
		headerName: "Stops Duration",
		flex: 13,
		minWidth: 130,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asDuration(p.value),
	},
	{
		field: "BarSetupDelaysDuration",
		headerName: "Bar Setup Duration",
		flex: 14,
		minWidth: 150,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asDuration(p.value),
	},
	{
		field: "ShiftUtilization",
		headerName: "Shift Utilization",
		flex: 13,
		minWidth: 130,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asPercent(p.value),
	},
	{
		field: "CobblesCount",
		headerName: "Cobbles",
		flex: 9,
		minWidth: 95,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asInteger(p.value),
	},
	{
		field: "CobblesDuration",
		headerName: "Time Between Cobbles",
		flex: 16,
		minWidth: 170,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asDuration(p.value),
	},
	{
		// The procedure does not return this one; the legacy screen derives it in
		// the browser and so does this.
		colId: "CobblesRate",
		headerName: "Cobbles Rate",
		flex: 12,
		minWidth: 120,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueGetter: (p) => (p.data ? cobblesRate(p.data) : null),
		valueFormatter: (p) =>
			p.value == null ? DASH : `${(p.value as number).toFixed(2)}%`,
	},
];

// The legacy screen opens on a single day: today to today.
const defaultDay = () => moment().startOf("day");

const URL_SCHEMA = {
	start: urlParam.date(defaultDay),
	end: urlParam.date(defaultDay),
};

export const ShiftsReport = () => {
	const [{ start, end }, setFilters, { resetValues }] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<ShiftsRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useShiftsData({ start: startStr, end: endStr });
	const rows = dataQuery.data ?? [];

	const handleGridReady = useCallback((event: GridReadyEvent<ShiftsRow>) => {
		gridApiRef.current = event.api;
	}, []);

	const handleStartChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ start: d });
		},
		[setFilters],
	);

	const handleEndChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ end: d });
		},
		[setFilters],
	);

	const handleReset = useCallback(() => {
		resetValues();
		dataQuery.refetch();
	}, [dataQuery, resetValues]);

	// The legacy workbook writes the three duration columns as h:m:s strings and
	// the rest as typed numbers, with the rate rounded to two decimals. Matched
	// here: two workbooks whose same column is in different units cannot be
	// compared, which is what this one is for.
	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "Shifts.xlsx",
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (DURATION_COLUMNS.has(colId)) return asDuration(params.value);
				if (colId === "CobblesRate")
					return params.value == null ? "" : Number(params.value.toFixed(2));
				return params.value ?? "";
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Shifts
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<ShiftsToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					onReset={handleReset}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			{/* No flex: this report always returns one row per crew, so a card
			    stretched to the viewport would be mostly empty. */}
			<Paper elevation={3} sx={{ overflow: "hidden" }}>
				<Box>
					{dataQuery.isLoading ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: 200,
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<AgGridReact<ShiftsRow>
							columnDefs={COLUMN_DEFS}
							rowData={rows}
							rowModelType="clientSide"
							domLayout="autoHeight"
							pagination={false}
							defaultColDef={{ resizable: true, sortable: true }}
							suppressContextMenu
							overlayNoRowsTemplate={NO_ROWS_OVERLAY}
							headerHeight={HEADER_HEIGHT}
							rowHeight={ROW_HEIGHT}
							onGridReady={handleGridReady}
						/>
					)}
				</Box>
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
